// contextAnchor — the active tab's Context anchor, pure.
//
// Context spine step 3a (.claude/analysis/context-model-tab-scoped.md §2.1:
// "a tab does not need a fresh, opaque identity to key Context by — its
// anchor already IS one"). `AppShellHost` derives the active tab off the
// (focused-leaf) pane tree; this module turns that tab into the `EntityRef`
// the Context BC keys by.
//
// Two tab kinds carry an honest anchor today, each keyed by its own id:
//   - a RESOURCE tab  → a whole-resource EntityRef (the source/material/export
//     the tab shows).
//   - a CONVERSATION tab → a conversation EntityRef, so the sources introduced
//     into that conversation surface in the ContextSideCard's Grounding section
//     and ground its turns (the same anchor `sendTurn`/introduce/drop/clear
//     already speak — the transport's `anchorWire` and the server contract
//     both accept the `conversation` kind).
//
// The `conversation` arm now covers a NASCENT (empty) conversation too — its
// client-minted id is a perfectly good `Core.Conversation.Ref` from the moment
// the tab opens (hero-is-initial-conversation-state §2.1), so there is no longer
// a hero case to special-case to `null`.
//
// The remaining kinds (Settings/Quality) read as `null` — there is no honest
// anchor for them (tab-scoped doc §5: "a field with no meaningful value for a
// whole class of instances is a lie about the type"), so the strip and the
// mention dispatch simply have nothing to target while one of those is focused.
// A new tab kind must state its anchor (or `null`) here — the exhaustive switch
// breaks until it does.

export { anchorForActiveTab };

import * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import type { ActiveTab } from "../components/Navigation/types.js";

const anchorForActiveTab = (active: ActiveTab): Core.EntityRef.Ref | null => {
    switch (active.type) {
        case "resource":
            return Core.Resource.Ref.mkWhole(
                Core.Resource.Id.mk(active.id),
                Core.Versioning.Version.v1,
            );
        case "conversation":
            return Core.Conversation.Ref.mk(
                Core.Conversation.Id.mk(active.id),
                Core.Versioning.Version.v1,
            );
        case "settings":
        case "home":
        case "studio":
        case "projects":
        case "knowledge":
        case "search":
        case "quality":
            return null;
        default:
            return exhaustSwitchCase(active);
    }
};
